// Same convention as the other analytics helpers: a missing measurement is an em
// dash, and a real 0 never collapses into it.
import { EM_DASH } from './rfmAnalysis';

// Re-exported so the tab renders "no measurement" the same way its formatters do.
export { EM_DASH };

/**
 * Format a monetary figure in the domain currency.
 * @param {number|null} [value]
 * @param {string} [currency] - ISO currency code from `state.domain.domain.currency`
 * @returns {string}
 */
export const formatCurrency = (value, currency) =>
  value == null ? EM_DASH : value.toLocaleString(navigator.language, currency ? { style: 'currency', currency } : {});

/**
 * Format a count (deliveries, recipients, conversions).
 * @param {number|null} [value]
 * @returns {string}
 */
export const formatCount = (value) => (value == null ? EM_DASH : value.toLocaleString(navigator.language));

/**
 * Format a 0..1 ratio as a percentage. Open and click rates count events rather
 * than unique recipients, so a ratio above 1 is legitimate (a recipient who
 * opens twice counts twice) and is deliberately not clamped to 100%. A null
 * ratio means the channel has no such signal at all — SMS, Viber, WhatsApp and
 * push report no opens — which is not the same as 0%.
 * @param {number|null} [value]
 * @returns {string}
 */
export const formatRatio = (value) =>
  value == null ? EM_DASH : `${(value * 100).toLocaleString(navigator.language, { maximumFractionDigits: 1 })}%`;

// A missing value behaves as 0 here, matching formatCurrency/formatCount/
// formatRatio which all render a missing value as an em dash rather than
// treating it as anything other than "no measurement". Without this,
// `undefined - 100` is NaN (unlike `null - 100`, which is -100), so a period
// where the endpoint omits a zero-valued key would render `+NaN%`.
const toNumber = (value) => Number(value) || 0;

/**
 * Magnitude of the change between the selected period and the previous one, as a
 * percentage. Follows SummaryCard: with no previous value to divide by, any
 * movement at all counts as a full 100%.
 * @param {number} [value]
 * @param {number} [previousValue]
 * @returns {number}
 */
export const changePercent = (value, previousValue) => {
  const current = toNumber(value);
  const previous = toNumber(previousValue);
  return previous ? Math.abs((current - previous) / previous) * 100 : current ? 100 : 0;
};

/**
 * Direction to render a change in, with SummaryCard's 3% dead-band so that noise
 * reads as flat. Unlike SummaryCard an exactly unchanged value is 'neutral'
 * rather than nothing at all, because every figure on this tab carries a delta
 * line — and 0% is inside the dead-band anyway.
 * @param {number} [value]
 * @param {number} [previousValue]
 * @returns {'up'|'down'|'neutral'}
 */
export const changeTone = (value, previousValue) => {
  if (changePercent(value, previousValue) < 3) {
    return 'neutral';
  }
  return toNumber(previousValue) > toNumber(value) ? 'down' : 'up';
};

/**
 * Name the conversion metric by the domain's own mapped event actions rather than
 * a generic word, so the tab says which events are actually being counted (e.g.
 * `cartCheckoutSuccess`). `label` is the endpoint's human-readable fallback for a
 * domain that has no purchase mapping at all.
 * @param {object} [conversionMetric] - `{ label, actions }` from the endpoint
 * @returns {string}
 */
export const formatConversionMetric = (conversionMetric = {}) => {
  const actions = conversionMetric.actions;
  if (Array.isArray(actions) && actions.length > 0) {
    return actions.join(', ');
  }
  return conversionMetric.label || 'Conversions';
};

/**
 * Whether a campaign's revenue can meaningfully be compared with the previous
 * period. A one-off campaign sends once, so it almost never runs in both periods
 * and its "change" would really be measuring the send date, not performance.
 * @param {object} [campaign] - a `topCampaigns` entry
 * @returns {boolean}
 */
export const isComparableAcrossPeriods = (campaign = {}) => campaign.mode !== 'oneOff';

/**
 * Whether live/not-live says anything about this campaign. It is a property of a
 * campaign that is meant to keep running: for a one-off, `live` goes false the
 * moment the send completes, so "Not live" would read as a problem when it
 * actually means the campaign did its job. A one-off's real states are
 * finished / running / scheduled / draft, which this tab does not report.
 *
 * A deleted campaign has none either: `live` is false for it too, and a grey
 * "Not live" badge would describe the deletion as a pause. Deletion is keyed on a
 * null `id`, the same signal the rest of the tab uses — `mode` is about which
 * states exist, not about whether the campaign does.
 * @param {object} [campaign] - a `topCampaigns` entry
 * @returns {boolean}
 */
export const hasLiveState = (campaign = {}) => campaign.id != null && campaign.mode !== 'oneOff';

// A campaign of type `workflow` lives under /workflows, everything else under
// /campaigns (src/pages/DomainPage.jsx:433,474). A campaign whose type the
// endpoint did not report falls through to /campaigns, which is where a simple
// campaign — the common case — belongs.
const campaignSegment = (type) => (type === 'workflow' ? 'workflows' : 'campaigns');

/**
 * Hash href for a campaign's details page. The router is a HashRouter, so every
 * absolute link is `#/accounts/...` (src/components/Profiles/ProfileEventLink.jsx:112).
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {object} args.campaign - a `topCampaigns` entry; needs `id`, and uses
 *   `type` and `mode` when present
 * @returns {string|null} null when there is no id to link to (deleted campaign)
 */
export const campaignHref = ({ accountId, domainId, campaign }) => {
  if (!campaign || campaign.id == null) {
    return null;
  }
  const base = `#/accounts/${accountId}/domains/${domainId}/${campaignSegment(campaign.type)}/${campaign.id}/details`;
  // The details pages read `mode` for breadcrumbs and back-navigation, and every
  // other link in the app passes it (src/components/Campaigns/CampaignsCard.jsx:66).
  return campaign.mode ? `${base}?mode=${campaign.mode}` : base;
};

/**
 * Hash href for a product block's details page.
 *
 * A product block is addressed through its page, not the domain
 * (src/pages/DomainPage.jsx:402, and src/pages/pages/recommenders/RecommendersPage.jsx:69
 * builds the same path), so a row needs both ids. `pageId` comes from the
 * endpoint alongside the name and is null for a deleted block — and undefined
 * against an API that predates it, which this treats the same way: no link
 * rather than a link to `pages/undefined`.
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {object} args.block - a `topProductBlocks` entry; needs `id` and `pageId`
 * @returns {string|null} null when the block cannot be addressed
 */
export const productBlockHref = ({ accountId, domainId, block }) => {
  if (!block || block.id == null || block.pageId == null) {
    return null;
  }
  return `#/accounts/${accountId}/domains/${domainId}/pages/${block.pageId}/recommenders/${block.id}/details`;
};

/**
 * Hash href for a banner's details page. Banners hang off the domain directly
 * (src/pages/DomainPage.jsx:489), so unlike a product block one id is enough —
 * see src/pages/pages/bannerBlocks/BannerBlocksPage.jsx:280 for the same path.
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {object} args.banner - a `topBanners` entry; needs `id`
 * @returns {string|null} null when there is no id to link to (deleted banner)
 */
export const bannerHref = ({ accountId, domainId, banner }) => {
  if (!banner || banner.id == null) {
    return null;
  }
  return `#/accounts/${accountId}/domains/${domainId}/banners/${banner.id}/details`;
};

/**
 * The change as signed text, so the direction survives being read without the
 * arrow's colour.
 * @param {number} [value]
 * @param {number} [previousValue]
 * @returns {string}
 */
export const formatChange = (value, previousValue) => {
  const tone = changeTone(value, previousValue);
  const sign = tone === 'up' ? '+' : tone === 'down' ? '-' : '';
  return `${sign}${changePercent(value, previousValue).toLocaleString(navigator.language, {
    maximumFractionDigits: 1,
  })}%`;
};
